/**
 * InstallPaperPrint — the job packet's two install tabs, filled in from what
 * the crew recorded, as chrome-free print pages (INSTALL-CHECK-01 +
 * REBATE-TEST-01, Seth 2026-09-25):
 *
 *   /jobs/:jobId/install-order — the Install Order: the customer and the
 *     equipment, every checklist line that fit the job with the crew's answer
 *     marked, lines 22–24 from Log the Charge, the notes, and the customer's
 *     sign-off ("The system operation has been explained and I understand it").
 *   /jobs/:jobId/rebate-test  — the Rebate Test Form in the sheet's own order
 *     and numbering, for the utility rebate paperwork.
 *
 * Black and white, letter portrait, one page each; browser Print → Save as
 * PDF makes the file to attach. Reads the same records as the job page, so
 * paper and screen can't drift. (Sheet typos fixed: "Orstat", "Duckwork",
 * "Wet Bult"; line 9 reads (8) − (7).)
 */
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useInvoiceConfig } from "./invoiceConfig";
import { useJob, useJobEquipment, useJobVisits, type Job } from "./jobs";
import { useJobRefrigerant } from "./refrigerant";
import { useInstallCheckItems, useJobChecks, checklistFor, checklistProgress, autoAnswers } from "./installChecklist";
import {
  useRebateTest, useRebateTestStatus, rebateCompute, SIZING_FIELDS, AIRFLOW_FIELDS, TXV_FIELDS, PISTON_FIELDS, DUCT_METHODS,
  type RebateData, type RebateField,
} from "./rebateTest";

const fmtDate = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
const fmtTime = (s: string | null | undefined) =>
  !s ? "" : new Date(s).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const PRINT_CSS = `@media print {
  @page { size: letter portrait; margin: 0.45in; }
  html, body { background: #fff !important; }
  .no-print { display: none !important; }
}
.paper { font-family: Arial, Helvetica, sans-serif; color: #000; }
.paper table { border-collapse: collapse; }`;

interface Place { add1: string | null; city: string | null; state: string | null; zip: string | null; phone1: string | null; phone2: string | null; phone3: string | null }

/** The customer block both papers share: name, street, city, zip, phones, install date, installer. */
function usePaperHeader(job: Job | null | undefined) {
  const { data: loc } = useQuery({
    queryKey: ["paper_location", job?.custno ?? null, job?.locno ?? null],
    enabled: !!job?.custno,
    queryFn: async (): Promise<Place | null> => {
      let q = supabase.from("mirror_location").select("add1, city, state, zip, phone1, phone2, phone3").eq("custno", job!.custno);
      if (job!.locno) q = q.eq("locno", job!.locno);
      const { data } = await q.order("locno").limit(1);
      return ((data ?? [])[0] ?? null) as Place | null;
    },
  });
  const { data: visits } = useJobVisits(job?.id ?? null);
  const name = useMemberName();
  return useMemo(() => {
    const install = (visits ?? []).find((v) => v.visit_role === "install" && v.assigned_empno) ?? (visits ?? []).find((v) => v.visit_role === "install") ?? null;
    const installDate = job?.crew_finished_at ?? install?.scheduled_date ?? null;
    return {
      name: job?.customer_name ?? job?.custno ?? "",
      add1: job?.site_address ?? loc?.add1 ?? "",
      city: job?.site_city ?? loc?.city ?? "",
      zip: loc?.zip ?? "",
      phones: [loc?.phone1, loc?.phone2, loc?.phone3].map((p) => (p ?? "").trim()).filter(Boolean),
      installDate,
      installer: name(install?.assigned_empno) ?? (job?.crew_finished_by ? job.crew_finished_by.split("@")[0] : ""),
    };
  }, [job, loc, visits, name]);
}

function Toolbar({ title, note }: { title: string; note: string }) {
  return (
    <div className="no-print mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
      <span className="font-semibold text-slate-800">{title}</span>
      <span className="text-slate-400">{note}</span>
      <button type="button" onClick={() => window.print()} className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-slate-800">Print</button>
    </div>
  );
}

/** A filled-in blank: the value on a rule, like the paper. */
function Blank({ v, className }: { v: React.ReactNode; className?: string }) {
  return <span className={`inline-block min-w-[1.5in] border-b border-black px-1 ${className ?? ""}`}>{v || " "}</span>;
}

/* ================================================================ Install Order */

export function InstallOrderPrintView() {
  const { jobId = "" } = useParams();
  const { data: job, isLoading } = useJob(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: logs } = useJobRefrigerant(jobId);
  const { data: items } = useInstallCheckItems();
  const { data: answers } = useJobChecks(jobId);
  const cfg = useInvoiceConfig();
  const h = usePaperHeader(job);
  const units = (equip ?? []).filter((e) => e.kind !== "material");
  const lines = checklistFor(items ?? [], units);
  const auto = autoAnswers(logs ?? []);
  const p = checklistProgress(lines, answers, auto);
  const charge = (logs ?? []).find((l) => l.kind === "charge") ?? null;
  const half = Math.ceil(lines.length / 2);
  const cols = [lines.slice(0, half), lines.slice(half)];
  const noNotes = p.no.map((l) => ({ l, note: answers?.get(l.key)?.note ?? null }));

  useEffect(() => { document.title = `Install Order · ${h.name || "Job"}`; }, [h.name]);

  if (isLoading) return <p className="p-6 text-sm text-slate-500">Loading…</p>;
  if (!job) return <p className="p-6 text-sm text-slate-600">That job wasn&apos;t found.</p>;

  const mark = (value: string | null, opt: "yes" | "no" | "na", label: string) => (
    <span className={`mx-0.5 inline-block px-1 ${value === opt ? "rounded-full border-[1.5px] border-black font-bold" : "text-slate-500"}`}>{label}</span>
  );

  return (
    <div className="paper mx-auto max-w-[8.5in] bg-white p-6 text-[12px] leading-snug print:max-w-none print:p-0">
      <style>{PRINT_CSS}</style>
      <Toolbar title="Install Order" note="The job packet's Install Order, filled in from the crew's phone." />

      <header className="border-b-2 border-black pb-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-[15px] font-bold uppercase tracking-wide">{cfg.name}</h1>
          <span className="text-[11px]">{cfg.addr1}, {cfg.addr2} · {cfg.phone}</span>
        </div>
        <h2 className="mt-0.5 text-[18px] font-bold">Install Order</h2>
      </header>

      <table className="mt-2 w-full text-[12px]">
        <tbody>
          <tr><td className="w-[14%] py-0.5 font-semibold">Job</td><td className="py-0.5">{job.title ?? ""}{job.source_dispatch ? ` · #${job.source_dispatch}` : ""}</td>
            <td className="w-[16%] py-0.5 font-semibold">Phone</td><td className="py-0.5">{h.phones.join(" · ")}</td></tr>
          <tr><td className="py-0.5 font-semibold">Name</td><td className="py-0.5">{h.name}</td>
            <td className="py-0.5 font-semibold">Comfort Advisor</td><td className="py-0.5">{job.sale_tech ?? ""}</td></tr>
          <tr><td className="py-0.5 font-semibold">Address</td><td className="py-0.5">{h.add1}</td>
            <td className="py-0.5 font-semibold">Installation Date</td><td className="py-0.5">{fmtDate(h.installDate)}</td></tr>
          <tr><td className="py-0.5 font-semibold">City · Zip</td><td className="py-0.5">{[h.city, h.zip].filter(Boolean).join(" · ")}</td>
            <td className="py-0.5 font-semibold">Installer</td><td className="py-0.5">{h.installer}</td></tr>
        </tbody>
      </table>

      <table className="mt-2 w-full border border-black text-[11.5px]">
        <thead>
          <tr className="border-b border-black bg-slate-100 text-left">
            <th className="px-1.5 py-0.5 font-semibold">Equipment</th><th className="px-1.5 py-0.5 font-semibold">Model</th><th className="px-1.5 py-0.5 font-semibold">Serial #</th>
          </tr>
        </thead>
        <tbody>
          {units.length === 0 && <tr><td colSpan={3} className="px-1.5 py-1 text-slate-500">No equipment on the job.</td></tr>}
          {units.map((u) => (
            <tr key={u.id} className="border-t border-slate-300">
              <td className="px-1.5 py-0.5">{u.category || "Unit"}{u.manufacturer ? ` · ${u.manufacturer}` : ""}</td>
              <td className="px-1.5 py-0.5 font-mono">{u.model ?? ""}</td>
              <td className="px-1.5 py-0.5 font-mono">{u.serial ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-2 gap-x-5">
        {cols.map((col, ci) => (
          <ol key={ci} className="space-y-0.5">
            {col.map((l, i) => {
              const n = ci * half + i + 1;
              const v = p.valueOf(l.key);
              return (
                <li key={l.key} className="flex items-baseline gap-1.5 border-b border-slate-300 py-[3px]">
                  <span className="w-5 shrink-0 text-right font-semibold">{n}</span>
                  <span className="min-w-0 flex-1">{l.label}</span>
                  {l.kind === "ynna"
                    ? <span className="shrink-0 whitespace-nowrap text-[11px]">{mark(v, "yes", "Y")}–{mark(v, "no", "N")}–{mark(v, "na", "NA")}</span>
                    : <span className="shrink-0 font-semibold">{v ? `${v}${l.unit ? ` ${l.unit}` : ""}` : "______"}</span>}
                </li>
              );
            })}
          </ol>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-x-4">
        <div><span className="font-semibold">{lines.length + 1}. Who soldered the line set?</span> <Blank v={charge?.brazed_by} className="min-w-[0.9in]" /></div>
        <div><span className="font-semibold">{lines.length + 2}. Leak test psi</span> <Blank v={charge?.leak_test_psi != null ? String(charge.leak_test_psi) : ""} className="min-w-[0.8in]" /></div>
        <div><span className="font-semibold">{lines.length + 3}. Microns vacuum</span> <Blank v={charge?.vacuum_microns != null ? String(charge.vacuum_microns) : ""} className="min-w-[0.8in]" /></div>
      </div>

      {/* The paper's comments box = what the checklist says is left (a No and
          the installer's note). The Install Complete "why" stays an office note
          on the job — it lists missing photos and the like, not for this page. */}
      <div className="mt-3">
        <div className="font-semibold">Additional Comments:</div>
        <div className="mt-0.5 min-h-[0.7in] border border-black px-2 py-1">
          {noNotes.map(({ l, note }) => <p key={l.key}>No — {l.label}{note ? `: ${note}` : ""}</p>)}
        </div>
      </div>

      <p className="mt-2">
        <span className="font-semibold">Notify Office of Job Status:</span>{" "}
        {job.crew_finished_at
          ? <>Yes — Install Complete sent to the office {fmtDate(job.crew_finished_at)} at {fmtTime(job.crew_finished_at)}</>
          : <>Not sent yet</>}
      </p>

      <div className="mt-3 border-t-2 border-black pt-2">
        <p className="font-semibold">The System Operation Has Been Explained and I Understand It:</p>
        <div className="mt-1 grid grid-cols-[1fr_auto] items-end gap-4">
          <div>
            <div className="flex items-end gap-2">
              <span className="font-semibold">Customer Signature:</span>
              <span className="inline-flex h-[0.6in] min-w-[2.6in] items-end border-b border-black">
                {job.signoff_signature && <img src={job.signoff_signature} alt="Customer signature" className="max-h-[0.58in] object-contain" />}
              </span>
            </div>
            {job.signoff_name && <div className="mt-0.5 pl-[1.3in] text-[11px]">{job.signoff_name}</div>}
          </div>
          <div><span className="font-semibold">Date:</span> <Blank v={fmtDate(job.signoff_at)} className="min-w-[1in]" /></div>
        </div>
        <div className="mt-2 grid grid-cols-[1fr_auto] items-end gap-4">
          <div><span className="font-semibold">Installer:</span> <Blank v={h.installer} className="min-w-[2.6in]" /></div>
          <div><span className="font-semibold">Date:</span> <Blank v={fmtDate(job.crew_finished_at)} className="min-w-[1in]" /></div>
        </div>
      </div>
      {lines.length > 0 && (
        <p className="mt-3 text-[9.5px] text-slate-500">Lines for equipment that isn&apos;t on this job are left off. From Pegasus — the same answers are on the job&apos;s Install Record.</p>
      )}
    </div>
  );
}

/* ============================================================ Rebate Test Form */

export function RebateTestPrintView() {
  const { jobId = "" } = useParams();
  const { data: job, isLoading } = useJob(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: row } = useRebateTest(jobId);
  const { data: status } = useRebateTestStatus(jobId);
  const h = usePaperHeader(job);
  const d: RebateData = row?.data ?? {};
  const comp = rebateCompute(d) as Record<string, number | null>;
  const cool = (equip ?? []).find((e) => ["ac", "heat-pump", "mini-split"].includes(e.equip_key ?? ""));
  const furn = (equip ?? []).find((e) => ["furnace", "air-handler"].includes(e.equip_key ?? ""));

  useEffect(() => { document.title = `Rebate Test Form · ${h.name || "Job"}`; }, [h.name]);

  if (isLoading) return <p className="p-6 text-sm text-slate-500">Loading…</p>;
  if (!job) return <p className="p-6 text-sm text-slate-600">That job wasn&apos;t found.</p>;

  const val = (f: RebateField): string => {
    const v = f.kind === "computed" ? comp[f.key] : d[f.key];
    if (v === null || v === undefined || v === "") return "";
    return String(v);
  };
  const Row = ({ n, label, v, dim }: { n: string; label: string; v: string; dim?: boolean }) => (
    <tr className="border-t border-black">
      <td className="w-12 border-r border-black px-1.5 py-[3px] text-center font-semibold">{n}</td>
      <td className={`border-r border-black px-1.5 py-[3px] uppercase ${dim ? "text-slate-400" : ""}`}>{label}</td>
      <td className="w-[1.8in] px-1.5 py-[3px] text-right font-semibold tabular-nums">{v}</td>
    </tr>
  );
  const Section = ({ title }: { title: string }) => (
    <tr className="border-t-2 border-black bg-slate-100"><td colSpan={3} className="px-1.5 py-[3px] font-bold uppercase">{title}</td></tr>
  );
  const txv = d.metering === "txv", piston = d.metering === "piston";

  return (
    <div className="paper mx-auto max-w-[8.5in] bg-white p-6 text-[12px] leading-snug print:max-w-none print:p-0">
      <style>{PRINT_CSS}</style>
      <Toolbar title="Rebate Test Form"
        note={row?.completed_at ? "Complete — print it and send it in with the rebate." : status?.needed ? "Not finished yet — blanks print empty." : "This job doesn't need one; printing what's there."} />

      <table className="w-full border-2 border-black">
        <tbody>
          <tr>
            <td rowSpan={4} className="w-[45%] border-r border-black px-2 align-middle text-[20px] font-bold">REBATE TEST FORM</td>
            <td className="border-b border-r border-black px-1.5 py-[3px] font-semibold">TEST DATE</td><td className="border-b border-black px-1.5 py-[3px]">{fmtDate(String(d.test_date ?? "") || null)}</td>
          </tr>
          <tr><td className="border-b border-r border-black px-1.5 py-[3px] font-semibold">TECH</td><td className="border-b border-black px-1.5 py-[3px]">{String(d.tech_name ?? "")}</td></tr>
          <tr><td className="border-b border-r border-black px-1.5 py-[3px] font-semibold">TECH ID</td><td className="border-b border-black px-1.5 py-[3px]">{String(d.tech_id ?? "")}</td></tr>
          <tr><td className="border-r border-black px-1.5 py-[3px] font-semibold">OUTSIDE TEMP</td><td className="px-1.5 py-[3px]">{d.outside_temp != null && d.outside_temp !== "" ? `${d.outside_temp}°F` : ""}</td></tr>
          {([
            ["NAME", h.name], ["ADDRESS", h.add1], ["CITY", [h.city, h.zip].filter(Boolean).join(" ")], ["PHONE", h.phones[0] ?? ""],
            ["AC MODEL", cool?.model ?? ""], ["FURNACE MODEL", furn?.model ?? ""], ["INSTALL DATE", fmtDate(h.installDate)],
          ] as const).map(([k, v]) => (
            <tr key={k} className="border-t border-black">
              <td className="border-r border-black px-2 py-[3px] font-semibold">{k}</td>
              <td colSpan={2} className="px-1.5 py-[3px]">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table className="mt-3 w-full border-2 border-black">
        <tbody>
          <Section title="Sizing" />
          {SIZING_FIELDS.map((f) => <Row key={f.key} n={f.n} label={f.label.replace(" — ", "-")} v={val(f) ? `${val(f)} A` : ""} />)}
          <Section title="Air Flow" />
          {AIRFLOW_FIELDS.map((f) => <Row key={f.key} n={f.n} label={f.key === "fan_speed" ? "CFM Fan Speed (L M MH H ECM)" : f.label} v={val(f) ? `${val(f)}${f.unit && f.kind !== "choice" ? ` ${f.unit}` : ""}` : ""} />)}
          <Section title={`TXV System${txv ? "" : piston ? " — not this system" : ""}`} />
          {TXV_FIELDS.map((f) => <Row key={f.key} n={f.n} label={f.label} v={txv && val(f) ? `${val(f)} ${f.unit ?? ""}`.trim() : ""} dim={piston} />)}
          <Section title={`Non-TXV System${piston ? "" : txv ? " — not this system" : ""}`} />
          {PISTON_FIELDS.map((f) => <Row key={f.key} n={f.n} label={f.label} v={piston && val(f) ? `${val(f)} ${f.unit ?? ""}`.trim() : ""} dim={txv} />)}
          <Section title="Ductwork — Which Method Was Used?" />
          {DUCT_METHODS.map((m) => (
            <tr key={m.v} className="border-t border-black">
              <td className="w-12 border-r border-black px-1.5 py-[3px] text-center font-semibold">{m.n}</td>
              <td className="border-r border-black px-1.5 py-[3px] uppercase">{m.label}</td>
              <td className="px-1.5 py-[3px] text-center text-[14px] font-bold">{d.duct_method === m.v ? "✓" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[9.5px] text-slate-500">
        Filled in on the installer&apos;s phone{row?.completed_at ? ` · completed ${fmtDate(row.completed_at)}` : ""}. Line 9 is the target split minus the actual split.
      </p>
    </div>
  );
}
